<script lang="ts" setup>
import BasicInput from '~/components/form-elements/BasicInput.vue'
import BasicInputDecorator from '~/components/form-elements/BasicInputDecorator.vue'
import { debounce } from '~/helpers/debounce-helper'

const props = withDefaults(
  defineProps<{
    fromValue: number | null
    toValue: number | null
    max: number
    min: number
    step?: number
  }>(),
  { step: 10 }
)

const emit = defineEmits<{
  (e: 'change', x: { minValue: number | null, maxValue: number | null }): void
}>()

const { fromValue, toValue } = toRefs(props)

const minValue = ref<number | null>(fromValue.value ? fromValue.value : props.min)
const maxValue = ref<number | null>(toValue.value ? toValue.value : props.max)

const debouncedEmitChange = debounce((signal: AbortSignal) => {
  if (signal.aborted) {
    return
  }

  emit('change', { minValue: minValue.value, maxValue: maxValue.value })
}, 800)

const leftPercent = computed(() => {
    return minValue.value !== null ? (minValue.value / props.max) * 100 : 0
  }
)

const rightPercent = computed(() => {
    return maxValue.value !== null ? (maxValue.value / props.max) * 100 : 0
  }
)

function updateFromInput (): void {
  if (minValue.value === null || maxValue.value === null) {
    return
  }

  minValue.value = Math.max(
    props.min,
    Math.min(maxValue.value - props.step, minValue.value)
  )

  maxValue.value = Math.min(
    props.max,
    Math.max(minValue.value + props.step, maxValue.value)
  )

  debouncedEmitChange()
}

function updateSlider (): void {
  if (minValue.value !== null && maxValue.value !== null) {
    if (minValue.value >= maxValue.value) {
      if (minValue.value - props.step <= props.min) {
        maxValue.value = minValue.value + props.step
      } else {
        minValue.value = maxValue.value - props.step
      }
    }

    debouncedEmitChange()
  }
}
</script>

<template>
  <div class="relative mx-auto h-auto max-w-full overflow-x-hidden overflow-y-visible">
    <div class="relative z-50 mt-6 max-w-full">
      <input
        v-model="minValue"
        class="pointer-events-none absolute h-1 w-full appearance-none bg-main-green"
        :max="max"
        :min="min"
        :step="step"
        type="range"
        @change="updateSlider"
      >

      <input
        v-model="maxValue"
        class="pointer-events-none absolute h-1 w-full appearance-none bg-main-green"
        :max="max"
        :min="min"
        :step="step"
        type="range"
        @change="updateSlider"
      >

      <div
        class="slider-track absolute h-1 bg-main-green"
        :style="{ left: `${leftPercent}%`, right: `${100 - rightPercent}%` }"
      />
    </div>

    <div class="flex items-center justify-between pt-6">
      <div class="w-1/2 pr-2">
        <BasicInputDecorator>
          <BasicInput
            id="minValue"
            v-model="minValue"
            class="[&_.basic-input-group]:!rounded-2xl"
            :is-border="false"
            :placeholder="'от ' + min"
            @input="updateFromInput"
          />
        </BasicInputDecorator>
      </div>
      <div>—</div>
      <div class="w-1/2 pl-2">
        <BasicInputDecorator>
          <BasicInput
            id="maxValue"
            v-model="maxValue"
            class="[&_.basic-input-group]:!rounded-2xl"
            :is-border="false"
            :placeholder="'до ' + max"
            @input="updateFromInput"
          />
        </BasicInputDecorator>
      </div>
    </div>

    <!--<div class="flex justify-between text-gray-700">-->
    <!--  <span>{{ minValue }}</span>-->
    <!--  <span>{{ maxValue }}</span>-->
    <!--</div>-->
  </div>
</template>

<style lang="scss" scoped>
input[type="range"] {
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 28px;
    height: 28px;
    background: #59ad3b;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid #59ad3b;
    pointer-events: all;
  }

  &::-moz-range-thumb {
    width: 28px;
    height: 28px;
    background: #59ad3b;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid #59ad3b;
    pointer-events: all;
  }
}

.slider-track {
  top: 50%;
  transform: translateY(-50%);
}
</style>
